import React, { useState, useEffect } from 'react';
import './RealTimeNotifications.css';

/**
 * RealTimeNotifications - Creates social proof through simulated user activity
 * Shows toast notifications of other users taking quizzes, getting results, sharing, etc.
 */
export const RealTimeNotifications = ({ 
  enabled = true,
  position = 'bottom-left', // 'top-left', 'top-right', 'bottom-left', 'bottom-right'
  maxNotifications = 3,
  displayDuration = 5000,
  interval = 8000,
  activityLevel = 'normal' // 'quiet', 'normal', 'busy', 'viral'
}) => {
  const [notifications, setNotifications] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  
  // Notification templates based on activity
  const notificationTemplates = {
    quizStart: [
      { icon: '🧠', text: '{name} from {location} just started "{quiz}"' },
      { icon: '✨', text: '{name} is taking "{quiz}" right now' },
      { icon: '🎯', text: '{name} began discovering their {trait}' },
      { icon: '🔥', text: '{count} people started "{quiz}" in the last minute' }
    ],
    quizComplete: [
      { icon: '🎉', text: '{name} just discovered they\'re {result}!' },
      { icon: '✅', text: '{name} completed "{quiz}" - {rarity}% rare!' },
      { icon: '🏆', text: '{name} unlocked {result} personality type' },
      { icon: '💫', text: '{name} scored in the top {percentile}%' }
    ],
    sharing: [
      { icon: '📤', text: '{name} shared their {result} results' },
      { icon: '💬', text: '"{comment}" - {name} about their results' },
      { icon: '🔗', text: '{name} invited {count} friends to compare' },
      { icon: '📱', text: '{name} posted results to {platform}' }
    ],
    achievements: [
      { icon: '🏅', text: '{name} earned "{achievement}" badge!' },
      { icon: '⚡', text: '{name} is on a {streak}-day streak!' },
      { icon: '🎖️', text: '{name} completed all {category} quizzes' },
      { icon: '👑', text: '{name} became a Quiz Master!' }
    ],
    viral: [
      { icon: '🚀', text: '"{quiz}" is trending - {count} taking now!' },
      { icon: '🔥', text: 'VIRAL: {count}+ completions in last hour' },
      { icon: '📈', text: '"{quiz}" jumped to #{rank} trending' },
      { icon: '⚡', text: 'Server load spike - everyone\'s taking "{quiz}"!' }
    ]
  };
  
  // Generate realistic fake data
  const generateNotification = () => {
    const names = [
      'Sarah', 'Mike', 'Emma', 'James', 'Olivia', 'Daniel', 'Sophia', 'Alex',
      'Emily', 'Ryan', 'Jessica', 'Tyler', 'Madison', 'Brandon', 'Ashley', 'Chris',
      'Taylor', 'Jordan', 'Morgan', 'Casey', 'Jamie', 'Sam', 'Avery', 'Riley'
    ];
    
    const locations = [
      'California', 'New York', 'Texas', 'Florida', 'London', 'Toronto', 
      'Sydney', 'Tokyo', 'Berlin', 'Paris', 'Dubai', 'Singapore',
      'Los Angeles', 'Chicago', 'Boston', 'Seattle', 'Denver', 'Miami'
    ];
    
    const quizzes = [
      'Personality Type Test', 'Career Compass', 'Love Language Quiz',
      'Emotional Intelligence', 'Leadership Style', 'Stress Response',
      'Communication Style', 'Learning Type', 'Team Role Assessment'
    ];
    
    const results = [
      'INTJ - The Architect', 'ENFP - The Campaigner', 'ISTJ - The Logistician',
      'ENTP - The Debater', 'INFJ - The Advocate', 'ESTP - The Entrepreneur'
    ];
    
    const platforms = ['Instagram', 'Twitter', 'Facebook', 'TikTok', 'LinkedIn'];
    
    const achievements = [
      'Quiz Explorer', 'Personality Expert', 'Self-Discovery Master',
      'Insight Seeker', 'Mind Reader', 'Psychology Pro'
    ];
    
    const categories = ['personality', 'career', 'relationship', 'lifestyle'];
    
    const comments = [
      'This is so accurate it\'s scary!',
      'My friends need to take this',
      'Finally understand myself',
      'Mind = blown 🤯',
      'Everyone should know their type',
      'This explains everything!'
    ];
    
    // Determine notification type based on activity level
    const typeWeights = {
      quiet: { quizStart: 0.4, quizComplete: 0.3, sharing: 0.2, achievements: 0.1, viral: 0 },
      normal: { quizStart: 0.3, quizComplete: 0.3, sharing: 0.2, achievements: 0.15, viral: 0.05 },
      busy: { quizStart: 0.25, quizComplete: 0.25, sharing: 0.25, achievements: 0.15, viral: 0.1 },
      viral: { quizStart: 0.2, quizComplete: 0.2, sharing: 0.25, achievements: 0.1, viral: 0.25 }
    };
    
    const weights = typeWeights[activityLevel];
    const random = Math.random();
    let type;
    let cumulative = 0;
    
    for (const [key, weight] of Object.entries(weights)) {
      cumulative += weight;
      if (random < cumulative) {
        type = key;
        break;
      }
    }
    
    const templates = notificationTemplates[type];
    const template = templates[Math.floor(Math.random() * templates.length)];
    
    // Fill in template variables
    const data = {
      name: names[Math.floor(Math.random() * names.length)],
      location: locations[Math.floor(Math.random() * locations.length)],
      quiz: quizzes[Math.floor(Math.random() * quizzes.length)],
      result: results[Math.floor(Math.random() * results.length)].split(' - ')[0],
      trait: ['personality', 'strengths', 'career path', 'love style'][Math.floor(Math.random() * 4)],
      count: Math.floor(Math.random() * 50) + 10,
      rarity: (Math.random() * 5 + 0.5).toFixed(1),
      percentile: Math.floor(Math.random() * 20) + 80,
      platform: platforms[Math.floor(Math.random() * platforms.length)],
      achievement: achievements[Math.floor(Math.random() * achievements.length)],
      streak: Math.floor(Math.random() * 20) + 3,
      category: categories[Math.floor(Math.random() * categories.length)],
      rank: Math.floor(Math.random() * 5) + 1,
      comment: comments[Math.floor(Math.random() * comments.length)]
    };
    
    let text = template.text;
    Object.entries(data).forEach(([key, value]) => {
      text = text.replace(`{${key}}`, value);
    });
    
    return {
      id: Date.now() + Math.random(),
      icon: template.icon,
      text,
      type,
      timestamp: Date.now()
    };
  };
  
  // Generate notifications at intervals
  useEffect(() => {
    if (!enabled || isPaused) return;
    
    const createNotification = () => {
      const notification = generateNotification();
      
      setNotifications(prev => {
        const updated = [...prev, notification];
        // Keep only max number of notifications
        if (updated.length > maxNotifications) {
          return updated.slice(-maxNotifications);
        }
        return updated;
      });
      
      // Auto-remove after duration
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }, displayDuration);
    };
    
    // Initial notification
    const initialDelay = Math.random() * 3000 + 1000;
    const initialTimer = setTimeout(createNotification, initialDelay);
    
    // Recurring notifications
    const timer = setInterval(createNotification, interval);
    
    return () => {
      clearTimeout(initialTimer);
      clearInterval(timer);
    };
  }, [enabled, isPaused, interval, displayDuration, maxNotifications, activityLevel]);
  
  // Pause on hover
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);
  
  // Remove specific notification
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };
  
  if (!enabled || notifications.length === 0) return null;
  
  return (
    <div 
      className={`notification-container ${position}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {notifications.map((notification, index) => (
        <div
          key={notification.id}
          className={`notification-toast ${notification.type}`}
          style={{
            animationDelay: `${index * 0.1}s`
          }}
        >
          <span className="notification-icon">{notification.icon}</span>
          <span className="notification-text">{notification.text}</span>
          <button 
            className="notification-close"
            onClick={() => removeNotification(notification.id)}
            aria-label="Close notification"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

export default RealTimeNotifications;